import { useMemo, useState, useCallback } from "react";
import { MessageCircle, LayoutGrid, Volume2, VolumeX } from "lucide-react";
import DOMPurify from "dompurify";
import { proxyImg } from "@/utils/imageProxy";

const BRAND_LOGO = proxyImg("https://customer-assets.emergentagent.com/job_cake-design-studio-5/artifacts/5xphm2ah_Sugar%20Bouquet%20Logo.png", 64, 80);
const WHATSAPP_URL = "https://wa.me/c/918884915270";

const DID_YOU_KNOW_TIPS = [
  "You can share any design with friends/family by hovering on the top right corner of the design and clicking the WhatsApp share button!",
  "You can easily switch between designs by clicking any design card \u2014 the one you select gets a checkmark!",
  "Type \u201Cshow me menu\u201D anytime to browse all 14 designer collections!",
  "All our cakes are 100% eggless with freshly baked cupcakes and signature buttercream \u2014 every petal is fully edible!",
  "You can mix 2 flavours in one bouquet! Try Mississippi Mud Chocolate + Dreamy Vanilla!",
  "Our cakes come with a handwritten note, candle, ribbon, and premium packaging \u2014 all included!",
  "We deliver in AC cars to ensure your buttercream flowers arrive in perfect condition!",
];

function getRandomTip(index) {
  return DID_YOU_KNOW_TIPS[index % DID_YOU_KNOW_TIPS.length];
}

function formatContent(text) {
  let formatted = text;
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(
    /https?:\/\/wa\.me\/[^\s)]+/g,
    '{{WHATSAPP_BUTTON}}'
  );
  const paragraphs = formatted.split('\n').filter(p => p.trim());
  return paragraphs.map((p) => {
    if (p.trim().startsWith('- ') || p.trim().startsWith('\u2022 ')) {
      return `<li>${p.trim().substring(2)}</li>`;
    }
    if (/^\d+\.\s/.test(p.trim())) {
      return `<li>${p.trim().replace(/^\d+\.\s/, '')}</li>`;
    }
    return `<p>${p}</p>`;
  }).join('');
}

function stripHtml(html) {
  return html.replace(/<[^>]*>/g, '').replace(/\{\{WHATSAPP_BUTTON\}\}/g, '');
}

function cleanTextForSpeech(text) {
  let clean = text;
  // Remove emojis and unicode symbols
  clean = clean.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}\u{2702}-\u{27B0}\u{1F680}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2B50}\u{2764}\u{FE0F}\u{200B}-\u{200F}\u{2028}-\u{202F}✅🔲✨💐🌸🎂🎉🎀🧁🎁💕❤️💍👰📦🏆🧺🌟✓★●►▶▪️•—–→←↑↓]/gu, '');
  // Remove markdown
  clean = clean.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,6}\s*/g, '');
  // Remove bullet points and list markers
  clean = clean.replace(/^[\s]*[-•]\s*/gm, '');
  // Remove URLs
  clean = clean.replace(/https?:\/\/[^\s)]+/g, '');
  // Convert newlines to sentence breaks
  clean = clean.replace(/\n{2,}/g, '. ').replace(/\n/g, '. ');
  // Remove leftover special chars
  clean = clean.replace(/[|~`{}[\]<>]/g, '');
  // Normalise whitespace and punctuation
  clean = clean.replace(/\s{2,}/g, ' ').replace(/\.\s*\./g, '.').replace(/,\s*,/g, ',').trim();
  return clean;
}

function splitIntoSentences(text) {
  return text
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 1);
}

// Pre-load voices — browsers load them asynchronously
let _cachedVoice = null;
let _voicesLoaded = false;

function _loadVoices() {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return;
  _voicesLoaded = true;

  // Priority: Indian female named voices > any en-IN female > en-IN > English female named > English female
  const matchers = [
    v => v.lang === 'en-IN' && /neerja|aditi|priya/i.test(v.name),
    v => v.lang === 'en-IN' && /female|woman/i.test(v.name),
    v => v.lang === 'en-IN' && !/male|rishi/i.test(v.name),
    v => v.lang === 'en-IN',
    v => /samantha|karen|victoria|moira|tessa|fiona/i.test(v.name) && v.lang.startsWith('en'),
    v => /female|woman/i.test(v.name) && v.lang.startsWith('en'),
    v => v.lang.startsWith('en') && !/male|david|james|daniel|mark|google uk male|alex|fred|tom/i.test(v.name),
  ];
  for (const match of matchers) {
    const found = voices.find(match);
    if (found) { _cachedVoice = found; return; }
  }
}

// Trigger voice loading immediately and on change
if (typeof window !== 'undefined' && window.speechSynthesis) {
  _loadVoices();
  window.speechSynthesis.onvoiceschanged = _loadVoices;
}

function ensureVoicesLoaded() {
  return new Promise((resolve) => {
    if (_voicesLoaded) { resolve(); return; }
    // Force a fresh load attempt
    _loadVoices();
    if (_voicesLoaded) { resolve(); return; }
    // Wait for onvoiceschanged event
    const handler = () => {
      _loadVoices();
      window.speechSynthesis.onvoiceschanged = null;
      resolve();
    };
    window.speechSynthesis.onvoiceschanged = handler;
    // Timeout fallback — don't wait forever
    setTimeout(() => { _loadVoices(); resolve(); }, 500);
  });
}

let _speakAborted = false;

async function speakText(text, onEnd) {
  window.speechSynthesis.cancel();
  _speakAborted = false;

  const cleanedText = cleanTextForSpeech(text);
  if (!cleanedText) { onEnd?.(); return; }

  const sentences = splitIntoSentences(cleanedText);
  if (sentences.length === 0) { onEnd?.(); return; }

  // Wait for voices to be available before speaking
  await ensureVoicesLoaded();

  let index = 0;

  function speakNext() {
    if (_speakAborted || index >= sentences.length) {
      onEnd?.();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(sentences[index]);
    utterance.rate = 0.92;
    utterance.pitch = 1.15;
    utterance.volume = 0.85;
    utterance.lang = 'en-IN';
    if (_cachedVoice) utterance.voice = _cachedVoice;

    utterance.onend = () => {
      index++;
      if (_speakAborted || index >= sentences.length) {
        onEnd?.();
        return;
      }
      // Pause between sentences for natural cadence
      setTimeout(speakNext, 280);
    };
    utterance.onerror = () => {
      index++;
      if (index >= sentences.length) onEnd?.();
      else setTimeout(speakNext, 100);
    };

    window.speechSynthesis.speak(utterance);
  }

  speakNext();
}

function stopSpeaking() {
  _speakAborted = true;
  window.speechSynthesis.cancel();
}

function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors duration-200 shadow-sm my-1"
      data-testid="whatsapp-chat-button"
    >
      <MessageCircle className="w-4 h-4" />
      Chat on WhatsApp
    </a>
  );
}

function RenderContent({ html }) {
  const sanitized = useMemo(() => DOMPurify.sanitize(html, { ALLOWED_TAGS: ['strong', 'em', 'p', 'li', 'br', 'span', 'ul', 'ol'], ALLOWED_ATTR: ['class'] }), [html]);

  if (!html.includes('{{WHATSAPP_BUTTON}}')) {
    return (
      <div
        className="message-content text-[15px] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }
  const parts = sanitized.split('{{WHATSAPP_BUTTON}}');
  return (
    <div className="message-content text-[15px] leading-relaxed">
      {parts.map((part, idx) => (
        <span key={`wa-part-${idx}`}>
          <span dangerouslySetInnerHTML={{ __html: part }} />
          {idx < parts.length - 1 && (
            <span className="block my-2">
              <WhatsAppButton />
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

export default function ChatMessage({ message, index, onBrowseAll }) {
  const isUser = message.role === "user";
  const [isSpeaking, setIsSpeaking] = useState(false);

  const htmlContent = useMemo(() => {
    if (!isUser) {
      return formatContent(message.content);
    }
    return null;
  }, [message.content, isUser]);

  const handleSpeak = useCallback(() => {
    if (isSpeaking) {
      stopSpeaking();
      setIsSpeaking(false);
      return;
    }
    setIsSpeaking(true);
    const plainText = stripHtml(htmlContent || message.content);
    speakText(plainText, () => setIsSpeaking(false));
  }, [isSpeaking, htmlContent, message.content]);

  const isWelcome = message.id === "welcome";

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} mt-6 animate-fade-in-up`}
      style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
      data-testid={`chat-message-${message.role}-${index}`}
    >
      {!isUser && (
        <div className="h-8 w-8 rounded-full overflow-hidden bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 mr-3 mt-1" data-testid="ai-avatar">
          <img
            src={BRAND_LOGO}
            alt="Sugar Bouquet"
            className="w-full h-full object-contain p-0.5"
            width={32}
            height={32}
            decoding="async"
          />
        </div>
      )}

      <div
        className={
          isUser
            ? "bg-gray-100 text-gray-900 rounded-2xl rounded-tr-sm px-5 py-3.5 max-w-[85%]"
            : "text-gray-900 max-w-[90%]"
        }
        data-testid={`message-bubble-${message.role}-${index}`}
      >
        {isUser ? (
          <>
            {message.imagePreview && (
              <img
                src={message.imagePreview}
                alt="Uploaded"
                className="w-32 h-32 object-cover rounded-lg mb-2"
                data-testid="user-uploaded-image"
              />
            )}
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
          </>
        ) : (
          <>
            <RenderContent html={htmlContent} />

            {/* Voice read-aloud button */}
            {!isWelcome && (
              <button
                onClick={handleSpeak}
                className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200 cursor-pointer ${
                  isSpeaking
                    ? "bg-black text-white"
                    : "text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                }`}
                data-testid="voice-read-btn"
                title={isSpeaking ? "Stop reading" : "Read aloud"}
              >
                {isSpeaking ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                {isSpeaking ? "Stop" : "Listen"}
              </button>
            )}

            {/* Did you know tip */}
            {!isWelcome && onBrowseAll && (
              <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg" data-testid="did-you-know-tip">
                <p className="text-[11px] text-amber-800 leading-relaxed">
                  <span className="font-semibold">Did you know?</span> {getRandomTip(index)}
                </p>
              </div>
            )}

            {/* Browse all designs button */}
            {onBrowseAll && !isWelcome && (
              <button
                onClick={onBrowseAll}
                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black text-white text-xs font-medium hover:bg-gray-800 transition-all duration-200 cursor-pointer shadow-sm"
                data-testid="browse-all-designs-btn"
              >
                <LayoutGrid className="w-3 h-3" />
                Click to see all designs
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
